import React, { useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './KycReview.scss'

import iconBack from '../../../../../Assets/Icon/AdminKycQueue/mna0yq87-x5v88dn.svg'
import iconChevron from '../../../../../Assets/Icon/AdminKycQueue/mna0yq87-1zo71dg.svg'
import iconDobInfo from '../../../../../Assets/Icon/AdminKycQueue/mna0yq87-8rzg0z8.svg'
import iconDocAction from '../../../../../Assets/Icon/AdminKycQueue/mna0yq88-c30fyvb.svg'
import iconApprove from '../../../../../Assets/Icon/AdminKycQueue/mna0yq87-ccvsgds.svg'
import iconReject from '../../../../../Assets/Icon/AdminKycQueue/mna0yq88-mufby5h.svg'

import idFront from '../../../../../Assets/Images/AdminKycQueue/mna0yq8x-35ox70q.png'
import idBack from '../../../../../Assets/Images/AdminKycQueue/mna0yq8x-ebphwpb.png'

import kycServices from 'Services/kycServices'
import { TimeConverter } from 'Helpers/TimeConverter'
import { formatString } from 'Helpers/formatString'
import { ModalWrapper } from 'Components'

type KycStatusRoute = 'pending' | 'approved' | 'rejected' | 'request-info'

type VerificationDocument = {
  url?: string
  fileUuid?: string
  mimetype?: string
  fileCategory?: string
  metadata?: { expiryDate?: string }
}

export type InvestorKycApplicationDetails = {
  id: string
  accountId?: string
  msisdn?: string
  emailAddress?: string
  fullName?: string
  companyName?: string
  businessType?: string
  dateOfBirth?: string
  gender?: string
  nationality?: string
  identityDocumentType?: string
  documentNumber?: string
  status?: string
  createdAt?: string
  verificationDocuments?: VerificationDocument[]

  investorName: string
  isPep: boolean
  pepDetails: string | null
  hasCriminalRecord: boolean
  criminalRecordDetails: string | null
  hasFinancialCrimeInvestigation: boolean
  statusHistory: {
    actor: string
    status: string
    dateTimeInISO8601: string
  }[]
}

const KycReview: React.FC = () => {
  const { status, applicantId } = useParams()
  const s = (status ?? 'pending') as KycStatusRoute
  const isValidStatus =
    s === 'pending' || s === 'approved' || s === 'rejected' || s === 'request-info'

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [application, setApplication] = useState<InvestorKycApplicationDetails | null>(null)
  const [preview, setPreview] = useState<{ title: string; url: string; mimetype?: string } | null>(
    null,
  )

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  useEffect(() => {
    if (!isValidStatus || !applicantId) return

    const fetchApplication = async () => {
      setLoading(true)
      setErrorMessage(null)
      try {
        const response = await kycServices.getSingle(applicantId)
        setApplication((response?.data?.data ?? null) as InvestorKycApplicationDetails | null)
      } catch (e) {
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load application')
        setApplication(null)
      } finally {
        setLoading(false)
      }
    }

    fetchApplication()
  }, [applicantId, isValidStatus])

  if (!isValidStatus || !applicantId) return <Navigate to='/blockchain/kyc-queue/pending' replace />

  const canApprove = Boolean(application?.id) && application?.status !== 'APPROVED'
  const canReject = Boolean(application?.id) && application?.status !== 'REJECTED'

  const titleName = application?.fullName ?? application?.companyName ?? '-'

  const statusLabel = (() => {
    switch (application?.status) {
      case 'PENDING_APPROVAL':
        return 'Pending Review'
      case 'APPROVED':
        return 'Approved'
      case 'REJECTED':
        return 'Rejected'
      default:
        return application?.status ? formatString(application.status) : 'Unknown'
    }
  })()
  const statusVariant =
    application?.status === 'APPROVED'
      ? 'approved'
      : application?.status === 'REJECTED'
        ? 'rejected'
        : 'pending'

  const formatDateOfBirth = (iso?: string) => {
    if (!iso) return null
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const ageFromDateOfBirth = (iso?: string) => {
    if (!iso) return null
    const dob = new Date(iso)
    if (Number.isNaN(dob.getTime())) return null
    const now = new Date()
    let age = now.getFullYear() - dob.getFullYear()
    const m = now.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1
    return age
  }

  const docLabel = (doc: VerificationDocument, idx: number) => {
    if (doc.fileCategory) return formatString(doc.fileCategory)
    return idx === 0 ? 'Front of ID' : idx === 1 ? 'Back of ID' : `Document ${idx + 1}`
  }

  const openPreview = (doc: VerificationDocument, idx: number) => {
    const url = doc.url
    if (!url) return
    setPreview({
      title: docLabel(doc, idx),
      url,
      mimetype: doc.mimetype,
    })
  }

  const previewIsPdf =
    preview?.mimetype?.includes('pdf') || preview?.url.toLowerCase().endsWith('.pdf')

  const onApprove = async () => {
    if (!application?.id) return
    if (!window.confirm('Approve this KYC application?')) return
    setLoading(true)
    setErrorMessage(null)
    try {
      await kycServices.approve(application.id)
      const response = await kycServices.getSingle(application.id)
      setApplication((response?.data?.data ?? null) as InvestorKycApplicationDetails | null)
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to approve application')
    } finally {
      setLoading(false)
    }
  }

  const onReject = async () => {
    if (!application?.id) return
    if (!window.confirm('Reject this KYC application?')) return
    setLoading(true)
    setErrorMessage(null)
    try {
      await kycServices.reject(application.id)
      const response = await kycServices.getSingle(application.id)
      setApplication((response?.data?.data ?? null) as InvestorKycApplicationDetails | null)
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to reject application')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='admin-kyc-review'>
      <div className='admin-kyc-review__breadcrumb'>
        <Link to={`/blockchain/kyc-queue/${s}`} className='admin-kyc-review__crumb'>
          <img src={iconBack} alt='' className='admin-kyc-review__crumb-icon' />
          <span>KYC Applications</span>
        </Link>
        <img src={iconChevron} alt='' className='admin-kyc-review__crumb-sep' />
        <span className='admin-kyc-review__crumb-current'>Applicant Details</span>
      </div>

      <div className='admin-kyc-review__top'>
        <div className='admin-kyc-review__title'>
          <span className='admin-kyc-review__title-label'>KYC Review:</span>
          <span className='admin-kyc-review__title-name'>{titleName}</span>
        </div>
        <span
          className={[
            'admin-kyc-review__status',
            statusVariant === 'approved'
              ? 'admin-kyc-review__status--approved'
              : statusVariant === 'rejected'
                ? 'admin-kyc-review__status--rejected'
                : 'admin-kyc-review__status--pending',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {statusLabel}
        </span>
      </div>

      <div className='row g-3 g-xl-4'>
        <div className='col-12 col-xl-6'>
          <div className='admin-kyc-card'>
            <div className='admin-kyc-card__title'>Personal Information</div>

            <div className='admin-kyc-info'>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Full Name</div>
                <div className='admin-kyc-info__value'>{application?.fullName ?? titleName}</div>
              </div>

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Date of Birth</div>
                <div className='admin-kyc-info__value admin-kyc-info__value--inline'>
                  <span>
                    {formatDateOfBirth(application?.dateOfBirth)
                      ? `${formatDateOfBirth(application?.dateOfBirth)}${
                          ageFromDateOfBirth(application?.dateOfBirth) !== null
                            ? ` (${ageFromDateOfBirth(application?.dateOfBirth)} years old)`
                            : ''
                        }`
                      : '-'}
                  </span>
                  <img src={iconDobInfo} alt='' className='admin-kyc-info__mini-icon' />
                </div>
              </div>

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Nationality</div>
                <div className='admin-kyc-info__value'>{application?.nationality ?? '-'}</div>
              </div>

              {/* <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Email Address</div>
                <div className='admin-kyc-info__value'>{application?.emailAddress ?? '-'}</div>
              </div> */}

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Company Name</div>
                <div className='admin-kyc-info__value'>{application?.companyName ?? '-'}</div>
              </div>

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Has Criminal Record</div>
                <div className='admin-kyc-info__value'>
                  {application?.hasCriminalRecord ? 'Yes' : 'No'}
                </div>
              </div>

              {application?.hasCriminalRecord === true && (
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Criminal Record Details</div>
                  <div className='admin-kyc-info__value'>
                    {application?.criminalRecordDetails ?? '-'}
                  </div>
                </div>
              )}

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Has Financial Crime Investigation</div>
                <div className='admin-kyc-info__value'>
                  {application?.hasFinancialCrimeInvestigation ? 'Yes' : 'No'}
                </div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Are you politically exposed</div>
                <div className='admin-kyc-info__value'>{application?.isPep ? 'Yes' : 'No'}</div>
              </div>
              {application?.isPep === true && (
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Pep Details</div>
                  <div className='admin-kyc-info__value'>{application?.pepDetails ?? '-'}</div>
                </div>
              )}

              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Submitted</div>
                <div className='admin-kyc-info__value'>{TimeConverter(application?.createdAt)}</div>
              </div>
            </div>
          </div>

          <div className='admin-kyc-card admin-kyc-card--notes mt-3 mt-xl-4'>
            <div className='admin-kyc-card__title'>Admin Notes (Internal)</div>
            <textarea className='admin-kyc-notes' placeholder='Add internal review notes' />
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='admin-kyc-card'>
            <div className='admin-kyc-card__title'>Identity Document</div>

            <div className='admin-kyc-doc-meta'>
              <div className='admin-kyc-doc-meta__row'>
                <div className='admin-kyc-doc-meta__label'>Document Type</div>
                <div className='admin-kyc-doc-meta__value'>
                  {application?.identityDocumentType
                    ? formatString(application.identityDocumentType)
                    : '-'}
                </div>
              </div>
              <div className='admin-kyc-doc-meta__row'>
                <div className='admin-kyc-doc-meta__label'>ID Number</div>
                <div className='admin-kyc-doc-meta__value'>
                  {application?.documentNumber ?? '-'}
                </div>
              </div>
            </div>

            <div className='admin-kyc-docs'>
              {application?.verificationDocuments?.length
                ? application?.verificationDocuments.map((doc, idx) => {
                    const d = doc as VerificationDocument
                    const url = d.url
                    return (
                      <div
                        key={d.fileUuid ?? `${idx}`}
                        className='admin-kyc-doc'
                        role={url ? 'button' : undefined}
                        tabIndex={url ? 0 : undefined}
                        onClick={() => openPreview(d, idx)}
                        onKeyDown={(e) => {
                          if (!url) return
                          if (e.key === 'Enter' || e.key === ' ') openPreview(d, idx)
                        }}
                      >
                        <div className='admin-kyc-doc__head'>
                          <span>{docLabel(d, idx)}</span>
                          {url ? (
                            <button
                              type='button'
                              className='admin-kyc-doc__head-action'
                              onClick={(e) => {
                                e.stopPropagation()
                                openPreview(d, idx)
                              }}
                            >
                              <img
                                src={iconDocAction}
                                alt=''
                                className='admin-kyc-doc__head-icon'
                              />
                            </button>
                          ) : (
                            <img src={iconDocAction} alt='' className='admin-kyc-doc__head-icon' />
                          )}
                        </div>
                        <div className='admin-kyc-doc__image'>
                          <img src={url ?? (idx === 0 ? idFront : idBack)} alt='' />
                        </div>
                      </div>
                    )
                  })
                : 'No document found'}
            </div>

            <div className='admin-kyc-actions'>
              <button
                type='button'
                className='admin-kyc-actions__btn admin-kyc-actions__btn--approve'
                onClick={onApprove}
                disabled={!canApprove || loading}
              >
                <img src={iconApprove} alt='' className='admin-kyc-actions__icon' />
                <span>Approve</span>
              </button>
              <button
                type='button'
                className='admin-kyc-actions__btn admin-kyc-actions__btn--reject'
                onClick={onReject}
                disabled={!canReject || loading}
              >
                <img src={iconReject} alt='' className='admin-kyc-actions__icon' />
                <span>Reject</span>
              </button>
              <Link
                to={`/blockchain/investors/all/${application?.accountId}`}
                type='button'
                className='admin-kyc-actions__btn admin-kyc-actions__btn--request text-decoration-none'
              >
                View Profile
              </Link>
            </div>
          </div>
        </div>
      </div>

      {!loading && errorMessage && <div className='mt-3'>{errorMessage}</div>}

      <ModalWrapper
        showModal={Boolean(preview)}
        closeModal={() => setPreview(null)}
        title={preview?.title ?? 'Document'}
        size='lg'
      >
        {preview?.url ? (
          previewIsPdf ? (
            <iframe
              title={preview?.title ?? 'Document'}
              src={preview.url}
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          ) : (
            <img
              src={preview.url}
              alt={preview?.title ?? 'Document'}
              style={{ width: '100%', height: 'auto' }}
            />
          )
        ) : null}
        {preview?.url ? (
          <div className='mt-3 d-flex justify-content-end'>
            <a
              href={preview.url}
              target='_blank'
              rel='noreferrer'
              className='btn btn-outline-secondary'
            >
              Open in new tab
            </a>
          </div>
        ) : null}
      </ModalWrapper>
    </div>
  )
}

export default KycReview
